/**
 * Single source of truth for deriving the Job Docs "operation context"
 * (`partNumber` / `operationId`) from a work-order-op and its surrounding data.
 *
 * The part number can live in four places depending on the flow, and each host
 * screen used to hand-roll its own fallback chain — which is how they drifted
 * (e.g. the APM `workOrder.partNumber` case was missing from some). This
 * collapses that to ONE precedence chain, a strict superset of every site's
 * original order:
 *
 *   1. sibling `partOperation.partNumber`  — SelectActivity's selectActivityOptions
 *   2. nested `op.partOperation.partNumber` — currentWorkOrderOp (KHub/ERP)
 *   3. `op.workOrder.partNumber`            — APM synthetic current op
 *   4. `job.part`                           — legacy job fallback (JobInfo/ERP)
 *
 * Op SELECTION and `activityMode` stay with the caller: the op source genuinely
 * differs per screen (job-only, getCurrentWorkOrderOp, or workOrderOp ||
 * currentWorkOrderOp) and so does activityMode.
 *
 * Guardrails (equivalence with prior behavior depends on these):
 *  - JobInfo must call with `op` undefined (job-only) — passing an op would add
 *    fallbacks it never had.
 *  - Operation-scoped callers (e.g. JobInfoERP) need not pass `job` — without
 *    it the `job.part` term stays inert and behavior is unchanged.
 *
 * `??` throughout so a numeric `0` part/operation id is preserved (0 is a value,
 * not absence).
 *
 * @param {object} [op] Work-order-op (may carry `partOperation`, `workOrder`,
 *   `sequenceNumber`). Pass `undefined` for the job-only (legacy) flow.
 * @param {object} [extras]
 * @param {object} [extras.siblingPartOperation] partOperation dispatched as a
 *   sibling of the op (SelectActivity's selectActivityOptions).
 * @param {object} [extras.job] Legacy job (`{ part, operation }`).
 * @returns {{ partNumber: string|number|undefined, operationId: string|number|undefined }}
 */
export function resolveJobDocsContext(op, { siblingPartOperation, job } = {}) {
  return {
    partNumber:
      siblingPartOperation?.partNumber ??
      op?.partOperation?.partNumber ??
      op?.workOrder?.partNumber ??
      job?.part,
    operationId: op?.sequenceNumber ?? job?.operation,
  };
}
